import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { RecentBuys } from "@/components/RecentBuys";
import { ArrowRight, Star, Truck, RefreshCw, Globe, ShieldCheck, Award, Instagram, MapPin, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { SeasonalBanner } from "@/components/SeasonalBanner";
import { DiwaliHero } from "@/components/DiwaliHero";
import { FeatureHighlights } from "@/components/FeatureHighlights";
import { VideoCodeBar } from "@/components/VideoCodeBar";
import { ElfsightWidget } from "@/components/ElfsightWidget";
import { organizationSchema, localBusinessSchema, websiteSchema, SITE } from "@/lib/seo";

const PRICE_TIERS = [
  { label: "Under $99", slug: "under-99", bg: "#E07A5F", shadow: "#B04A32" },
  { label: "Under $150", slug: "under-150", bg: "#2A9D8F", shadow: "#1B6B61" },
  { label: "Under $200", slug: "under-200", bg: "#E4A11B", shadow: "#B07908" },
  { label: "Under $250", slug: "under-250", bg: "#9B2C6F", shadow: "#6C1B4C" },
];

// Curated occasion collections (US-audience-friendly titles)
const OCCASIONS = [
  { slug: "garba", title: "Navratri & Garba", sub: "Chaniya cholis & twirl sets" },
  { slug: "sharara-suits", title: "Festive Vibes", sub: "Shararas & festive sets" },
  { slug: "haldi-ceremony", title: "Haldi Glow", sub: "Sunny yellows for the Haldi" },
  { slug: "henna-party", title: "Mehndi Night", sub: "Vibrant picks for Mehndi" },
  { slug: "sangeet-ceremony", title: "Sangeet Ready", sub: "Dance-floor glam" },
  { slug: "wedding-ceremony-baraat", title: "Baraat & Big Day", sub: "Grand wedding-day looks" },
  { slug: "bridal-collection", title: "The Wedding Edit", sub: "Bridal & reception glamour" },
];

const shopImg = (url, w) => (url ? url + (url.includes("?") ? "&" : "?") + `width=${w}` : url);

const Hero = ({ slides }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    if (!slides.length) return;
    const id = setInterval(() => setI((v) => (v + 1) % slides.length), 5500);
    return () => clearInterval(id);
  }, [slides.length]);
  if (!slides.length) return <div className="h-[60vh] bg-[#EFEFEA]" />;
  const slide = slides[i];

  const renderDots = (className, prefix) => (
    <div className={className}>
      {slides.map((_, idx) => (
        <button key={idx} onClick={() => setI(idx)} data-testid={`${prefix}${idx}`} aria-label={`Slide ${idx + 1}`}
          className={`h-1.5 transition-all duration-300 ${idx === i ? "w-8 bg-[#FFFFFF]" : "w-4 bg-[#FFFFFF]/40"}`} />
      ))}
    </div>
  );

  return (
    <section className="bg-[#1A1A1A]" data-testid="hero">
      {/* Laptop / desktop: editorial split — portrait image shown near native size (crisp, minimal crop) */}
      <div className="hidden lg:grid grid-cols-[1fr_1.15fr] h-[80vh] min-h-[560px] max-h-[780px]">
        <div className="relative z-10 flex flex-col justify-center px-14 xl:px-20">
          <motion.div key={i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="text-[#FFFFFF] max-w-md">
            <div className="overline text-[#B84A3B] mb-4">New Festive Styles · Added Daily</div>
            <h2 className="font-display text-6xl xl:text-7xl leading-[0.95] tracking-tight">{slide.title}</h2>
            <p className="mt-5 text-lg text-[#FFFFFF]/80 font-body">Shop the {slide.title.toLowerCase()} edit — handpicked festive &amp; wedding looks.</p>
            <Link to={slide.to} data-testid="hero-cta"
              className="inline-flex items-center gap-2 mt-8 bg-[#FFFFFF] text-[#1A1A1A] px-8 py-4 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#B84A3B] hover:text-white transition-colors duration-300">
              Shop {slide.title} <ArrowRight size={14} />
            </Link>
          </motion.div>
          {renderDots("flex gap-2 mt-12", "hero-dot-")}
        </div>
        <div className="relative overflow-hidden">
          {slides.map((s, idx) => (
            <img key={idx} src={shopImg(s.img, 1400)} alt={s.title} loading={idx === 0 ? "eager" : "lazy"}
              className={`absolute inset-0 w-full h-full object-cover object-[center_18%] transition-opacity duration-[1200ms] ${idx === i ? "opacity-100" : "opacity-0"}`} />
          ))}
          <div className="absolute inset-y-0 left-0 w-28 bg-gradient-to-r from-[#1A1A1A] to-transparent" />
        </div>
      </div>

      {/* Mobile / tablet: full-bleed overlay */}
      <div className="lg:hidden relative h-[74vh] min-h-[480px] overflow-hidden">
        {slides.map((s, idx) => (
          <img key={idx} src={shopImg(s.img, 900)} alt={s.title} loading={idx === 0 ? "eager" : "lazy"}
            className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-[1200ms] ${idx === i ? "opacity-100" : "opacity-0"}`} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/25 to-black/35" />
        <div className="relative h-full px-5 sm:px-8 flex flex-col justify-end pb-14">
          <motion.div key={i} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="max-w-md text-[#FFFFFF]">
            <div className="overline text-[#FFFFFF]/80 mb-3">New Festive Styles · Added Daily</div>
            <h2 className="font-display text-5xl sm:text-6xl leading-[0.95] tracking-tight">{slide.title}</h2>
            <p className="mt-3 text-base text-[#FFFFFF]/90 font-body">Shop the {slide.title.toLowerCase()} edit — handpicked festive &amp; wedding looks.</p>
            <Link to={slide.to} data-testid="hero-cta-mobile"
              className="inline-flex items-center gap-2 mt-6 bg-[#FFFFFF] text-[#1A1A1A] px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.2em] uppercase hover:bg-[#B84A3B] hover:text-white transition-colors duration-300">
              Shop {slide.title} <ArrowRight size={14} />
            </Link>
          </motion.div>
        </div>
        {renderDots("absolute bottom-5 right-6 flex gap-2 z-10", "hero-dot-m-")}
      </div>
    </section>
  );
};

export default function Home() {
  const { addItem } = useCart();
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);
  const [recentlyUpdated, setRecentlyUpdated] = useState([]);
  const [lehengas, setLehengas] = useState([]);
  const [heroSlugs, setHeroSlugs] = useState([]);
  const [reviewMeta, setReviewMeta] = useState({ rating: 4.9, total: 227 });

  useEffect(() => {
    (async () => {
      const [c, p, r, ru, lh, hero] = await Promise.all([
        api.get("/collections"), api.get("/products?limit=60"), api.get("/reviews"),
        api.get("/products?sort=updated&limit=8"),
        api.get("/products?collection=lehenga-choli&sort=newest&limit=12"),
        api.get("/settings/hero").catch(() => ({ data: { slugs: [] } })),
      ]);
      setCollections(c.data); setProducts(p.data); setRecentlyUpdated(ru.data); setLehengas(lh.data);
      setHeroSlugs(hero?.data?.slugs || []);
      if (r.data.rating) setReviewMeta({ rating: r.data.rating, total: r.data.total || 0 });
    })();
  }, []);

  const onAdd = (product, size) => addItem(product, size);

  const bestsellers = useMemo(() => {
    if (lehengas.length) return lehengas.slice(0, 8);
    const seen = new Set();
    return [...products.filter((p) => p.badge === "Bestseller"), ...products]
      .filter((p) => (seen.has(p.slug) ? false : seen.add(p.slug))).slice(0, 8);
  }, [lehengas, products]);

  const newArrivals = useMemo(() => {
    // Recently updated pieces across ALL collections (new or edited — both float up).
    if (recentlyUpdated.length >= 4) return recentlyUpdated.slice(0, 8);
    const na = products.filter((p) => p.collections.includes("new-arrivals"));
    return (na.length >= 4 ? na : products.slice(8, 16)).slice(0, 8);
  }, [recentlyUpdated, products]);

  const heroSlides = useMemo(() => {
    const withImg = collections.filter((c) => c.image);
    const bySlug = Object.fromEntries(withImg.map((c) => [c.slug, c]));
    // Admin-selected collections take priority (in the chosen order).
    const picked = heroSlugs.map((s) => bySlug[s]).filter(Boolean);
    if (picked.length) {
      return picked.map((c) => ({ img: c.image, title: c.title, to: `/shop?collection=${c.slug}` }));
    }
    // Fallback: auto-select by keyword preference.
    const prefer = ["bridal", "wedding", "lehenga", "saree", "new", "party"];
    const scored = [...withImg].sort((a, b) => {
      const s = (c) => prefer.findIndex((k) => c.slug.toLowerCase().includes(k) || c.title.toLowerCase().includes(k));
      const sa = s(a), sb = s(b);
      return (sa === -1 ? 99 : sa) - (sb === -1 ? 99 : sb);
    });
    return scored.slice(0, 3).map((c) => ({ img: c.image, title: c.title, to: `/shop?collection=${c.slug}` }));
  }, [collections, heroSlugs]);
  const circleCols = collections.slice(0, 12);
  const occasions = useMemo(() => {
    const bySlug = Object.fromEntries(collections.map((c) => [c.slug, c]));
    return OCCASIONS.map((o) => ({ ...o, image: bySlug[o.slug]?.image })).filter((o) => o.image);
  }, [collections]);
  const insta = useMemo(() => products.filter((p) => p.images?.length).slice(0, 6).map((p) => p.images[0]), [products]);

  return (
    <div data-testid="home-page">
      <Seo path="/" jsonLd={[organizationSchema(), websiteSchema(), localBusinessSchema()]} />
      <SeasonalBanner />
      <DiwaliHero />
      <VideoCodeBar />
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8">
        <div className="flex gap-6 sm:gap-8 overflow-x-auto pb-3 no-scrollbar" data-testid="collection-circles">
          {circleCols.map((c) => (
            <Link key={c.slug} to={`/shop?collection=${c.slug}`} className="flex-shrink-0 flex flex-col items-center gap-2.5 group" data-testid={`circle-${c.slug}`}>
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden border border-[#E5E5DF] group-hover:border-[#B84A3B] transition-colors">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
              </div>
              <span className="text-[0.7rem] font-body text-[#1A1A1A] group-hover:text-[#B84A3B] transition-colors whitespace-nowrap max-w-[6rem] truncate">{c.title}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* Bulk bridesmaid orders — pre-order catalog for upcoming weddings (directs to WhatsApp) */}
      <section className="bg-[#1A1A1A] text-[#FFFFFF] mt-14" data-testid="bulk-bridesmaid-section">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 items-stretch">
          <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-14 lg:py-20 order-2 lg:order-1">
            <div className="overline text-[#B84A3B] mb-3">Now Accepting Bulk Orders</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.02]">Bridesmaid Dresses · 2026–27 Wedding Collection</h2>
            <p className="mt-5 text-base text-[#FFFFFF]/80 font-body leading-relaxed max-w-lg">
              Dress your whole bridal party in matching styles. Message us on WhatsApp and we'll send you
              the full pre-order catalog with pictures, prices and size options — then place your bulk order right in the chat.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Kurti Connection! Please send me the bridesmaid dresses catalog for bulk orders — I'm planning for an upcoming wedding.")}`}
                target="_blank" rel="noreferrer" data-testid="bulk-bridesmaid-whatsapp"
                className="inline-flex items-center gap-2 bg-[#25D366] text-[#0b3d1e] px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:opacity-90 transition-opacity">
                <MessageCircle size={14} /> Get the Catalog on WhatsApp
              </a>
            </div>
          </div>
          <div className="relative min-h-[300px] lg:min-h-[460px] order-1 lg:order-2">
            <img
              src="/images/bridesmaid-squad.jpg"
              alt="Twelve bridesmaids in matching purple lehenga sets — bulk pre-orders for weddings"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      <FeatureHighlights />

      <div className="bg-[#1A1A1A] text-[#FFFFFF]" data-testid="ship-trust-banner">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-3.5 flex items-center justify-center gap-3 text-center">
          <Truck size={18} strokeWidth={1.6} className="text-[#B84A3B] flex-shrink-0" />
          <p className="font-body text-[0.8rem] sm:text-sm tracking-wide">
            <b className="font-semibold">In-Stock &amp; Ready to Ship from Schaumburg, IL</b>
            <span className="hidden sm:inline"> — Delivered in 2–5 Days across the USA</span>
            <span className="sm:hidden"> · 2–5 Day US Delivery</span>
            {" · "}
            <a href={SITE.doordash} target="_blank" rel="noopener noreferrer" data-testid="doordash-banner-link"
              className="underline underline-offset-2 decoration-[#F0B429] hover:text-[#F0B429] transition-colors">
              Same-day via DoorDash within 25 mi
            </a>
          </p>
        </div>
      </div>

      <div className="bg-[#B84A3B] text-[#FFFFFF] py-3">
        <Marquee speed={45} gradient={false}>
          {["Free U.S. shipping over $75", "End of Season Sale — up to 70% off", "Bridal & guest lehengas @ $399 flat", "Easy 14-day returns", "New styles added daily"].map((t, i) => (
            <span key={i} className="mx-8 font-display text-xl italic">{t} <span className="mx-2 not-italic">✦</span></span>
          ))}
        </Marquee>
      </div>

      <RecentBuys />

      {/* Value proposition + primary H1 */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-12 text-center" data-testid="home-intro">
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] max-w-4xl mx-auto">
          Indian Ethnic Wear &amp; Wedding Attire in the USA
        </h1>
        <p className="mt-5 text-base sm:text-lg text-[#6E6E69] font-body max-w-2xl mx-auto leading-relaxed">
          Kurtis, sarees, lehengas, party wear &amp; jewelry for women, men &amp; kids — handpicked for weddings, Diwali,
          Navratri and every celebration. Shop our Schaumburg boutique near Chicago or order online, shipped nationwide.
        </p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          {[
            { label: "Shop Party Wear", to: "/shop?collection=reception-cocktail-party" },
            { label: "Shop Lehengas", to: "/shop?collection=lehenga-choli" },
            { label: "Shop Sarees", to: "/shop?collection=sarees" },
            { label: "Shop Kurtis", to: "/shop?collection=kurtis" },
          ].map((b) => (
            <Link key={b.to} to={b.to} data-testid={`intro-cta-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="inline-flex items-center gap-2 border border-[#1A1A1A] px-6 py-3 text-[0.72rem] font-mono tracking-[0.15em] uppercase text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FFFFFF] transition-colors">
              {b.label}
            </Link>
          ))}
          <Link to="/indian-boutique-schaumburg" data-testid="intro-cta-visit-store"
            className="inline-flex items-center gap-2 bg-[#B84A3B] text-white px-6 py-3 text-[0.72rem] font-mono tracking-[0.15em] uppercase hover:bg-[#1A1A1A] transition-colors">
            Visit Our Store
          </Link>
        </div>
      </section>

      {/* Trust signals */}
      <section className="border-b border-[#E5E5DF]" data-testid="trust-bar">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-7 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-[#E5E5DF]">
          <div className="flex items-center justify-center gap-3 py-3 sm:py-0">
            <div className="flex text-[#B84A3B]">{[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} fill="currentColor" strokeWidth={0} />)}</div>
            <div className="text-left"><div className="font-display text-xl leading-none">{reviewMeta.rating} / 5</div><div className="text-[0.68rem] font-mono tracking-wide text-[#6E6E69] mt-1">Google Rating · {reviewMeta.total} reviews</div></div>
          </div>
          <div className="flex items-center justify-center gap-3 py-3 sm:py-0">
            <Award size={24} strokeWidth={1.4} className="text-[#B84A3B]" />
            <div className="text-left"><div className="font-display text-xl leading-none">10 Years</div><div className="text-[0.68rem] font-mono tracking-wide text-[#6E6E69] mt-1">Serving families since 2016</div></div>
          </div>
          <div className="flex items-center justify-center gap-3 py-3 sm:py-0">
            <ShieldCheck size={24} strokeWidth={1.4} className="text-[#B84A3B]" />
            <div className="text-left"><div className="font-display text-xl leading-none">Trusted</div><div className="text-[0.68rem] font-mono tracking-wide text-[#6E6E69] mt-1">Secure checkout · Ships Globally</div></div>
          </div>
        </div>
      </section>

      <ProductRow title="New Arrivals" subtitle="Fresh styles — newly added & just updated" to="/shop" products={newArrivals} onAdd={onAdd} newBadge />

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14" data-testid="occasions-section">
        <Reveal>
          <div className="mb-8">
            <div className="overline text-[#B84A3B] mb-2">Shop by Occasion</div>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Special Collections by Occasion</h2>
            <p className="text-sm text-[#6E6E69] mt-1">From Onam to the big wedding day — dressed for every celebration.</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {occasions.map((c, i) => (
            <Reveal key={c.slug} delay={(i % 4) * 0.06}>
              <Link to={`/shop?collection=${c.slug}`} data-testid={`occasion-${c.slug}`} className="group relative block aspect-[4/5] overflow-hidden bg-[#EFEFEA]">
                <img src={shopImg(c.image, 600)} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[800ms]" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                <div className="absolute bottom-0 left-0 p-5 text-[#FFFFFF]">
                  <h3 className="font-display text-2xl sm:text-3xl tracking-tight leading-none">{c.title}</h3>
                  <p className="text-[0.72rem] text-[#FFFFFF]/85 mt-1 font-body">{c.sub}</p>
                  <span className="inline-flex items-center gap-1.5 mt-2 text-[0.62rem] font-mono tracking-[0.18em] uppercase border-b border-[#FFFFFF] pb-0.5">Explore <ArrowRight size={12} /></span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#7B2D43] text-[#FFFFFF]" data-testid="outfit-finder-banner">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 sm:py-16 flex flex-col sm:flex-row items-center justify-between gap-8">
          <Reveal>
            <div className="max-w-xl text-center sm:text-left">
              <div className="overline text-[#F5C518] mb-2">Wedding season? Free styling tool</div>
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight leading-tight">Haldi to Reception — find your perfect outfit &amp; size in 60 seconds</h2>
              <p className="text-sm text-[#FFFFFF]/80 mt-3 font-body">Tell us the functions you&apos;re attending, add your measurements, and we&apos;ll match you with outfits and your boutique-perfect size.</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <Link to="/outfit-finder" data-testid="outfit-finder-cta"
              className="inline-flex items-center gap-2.5 bg-[#FFFFFF] text-[#1A1A1A] px-8 py-4 text-[0.72rem] font-mono tracking-[0.22em] uppercase hover:bg-[#F5C518] transition-colors duration-300 shrink-0">
              Find My Outfit <ArrowRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8">
        <Reveal><h2 className="font-display text-3xl sm:text-4xl tracking-tight mb-6">Shop by Price</h2></Reveal>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {PRICE_TIERS.map((t, i) => (
            <Reveal key={t.label} delay={i * 0.06}>
              <Link to={`/shop?collection=${t.slug}`} data-testid={`price-tier-${t.slug}`}
                style={{ backgroundColor: t.bg, boxShadow: `0 7px 0 ${t.shadow}` }}
                className="group block rounded-2xl px-6 py-9 text-center text-white transition-transform duration-150 ease-out hover:-translate-y-1 active:translate-y-1.5 will-change-transform">
                <span className="font-display text-3xl sm:text-4xl drop-shadow-sm">{t.label}</span>
                <span className="block mt-2.5 text-[0.68rem] font-mono tracking-[0.2em] uppercase text-white/90">
                  Shop now
                  <ArrowRight size={13} className="inline-block ml-1.5 -mt-0.5 group-hover:translate-x-1 transition-transform" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      <ProductRow title="Bestsellers" subtitle={`Loved by ${reviewMeta.total}+ happy customers`} to="/shop?collection=lehenga-choli" products={bestsellers} onAdd={onAdd} />

      <RecentlyViewed title="Recently Viewed" subtitle="Jump back into pieces you loved" />

      <section className="border-y border-[#E5E5DF] mt-14">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {[[Truck, "Free U.S. Shipping", "On orders over $75"], [Globe, "Ships Globally", "Fast worldwide delivery"], [RefreshCw, "Easy Returns", "14-day hassle-free"], [ShieldCheck, "Secure Checkout", "Powered by Shopify"]].map(([Icon, t, s], i) => (
            <Reveal key={t} delay={i * 0.06}>
              <div className="flex items-center gap-3">
                <Icon size={26} strokeWidth={1.3} className="text-[#B84A3B]" />
                <div><div className="font-body text-sm font-semibold">{t}</div><div className="text-[0.72rem] text-[#6E6E69]">{s}</div></div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16" data-testid="reviews-section">
        <ElfsightWidget appId="9299c3c5-7202-47e9-9587-b4edf469d709" />
      </section>

      {/* Visit our boutique — bridges local showroom trust */}
      <section className="bg-[#1A1A1A] text-[#FFFFFF]" data-testid="visit-boutique">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 items-stretch">
          <div className="relative min-h-[340px] lg:min-h-[560px]">
            <img
              src="https://customer-assets-7cd3h4nn.emergentagent.net/job_kurti-showcase-7/artifacts/fu7jd0db_IMG_4383.png"
              alt="Kurti Connection boutique storefront in Schaumburg, IL"
              loading="lazy"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
          <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-16 py-12 lg:py-20">
            <div className="overline text-[#B84A3B] mb-3">Visit Us In-Store</div>
            <h2 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.02]">Visit Our Chicago-Area Boutique</h2>
            <p className="mt-5 text-base text-[#FFFFFF]/80 font-body leading-relaxed max-w-lg">
              We&apos;re a real, family-run boutique in Schaumburg, IL — not a dropshipper. Come touch the fabrics,
              try pieces on, and get same-day alterations. Every online order ships fast from our shelves here in the USA.
            </p>

            <div className="flex items-center gap-3 mt-6">
              <div className="flex text-[#B84A3B]">{[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} fill="currentColor" strokeWidth={0} />)}</div>
              <span className="font-body text-sm text-[#FFFFFF]/90">{reviewMeta.rating} / 5 on Google · {reviewMeta.total} reviews · 10 Years in Business</span>
            </div>

            <div className="flex items-start gap-3 mt-5 text-[#FFFFFF]/90">
              <MapPin size={18} strokeWidth={1.6} className="text-[#B84A3B] mt-0.5 flex-shrink-0" />
              <span className="font-body text-sm">183 W Golf Rd, Schaumburg, IL 60195</span>
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              <a href="https://www.google.com/maps/dir/?api=1&destination=Kurti+Connection+183+W+Golf+Rd+Schaumburg+IL+60195"
                target="_blank" rel="noreferrer" data-testid="boutique-directions"
                className="inline-flex items-center gap-2 bg-[#FFFFFF] text-[#1A1A1A] px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] hover:text-white transition-colors">
                Get Directions <ArrowRight size={14} />
              </a>
              <a href="tel:+12242461461" data-testid="boutique-call"
                className="inline-flex items-center gap-2 border border-[#FFFFFF]/40 text-[#FFFFFF] px-7 py-3.5 text-[0.72rem] font-mono tracking-[0.18em] uppercase hover:border-[#B84A3B] hover:text-[#B84A3B] transition-colors">
                Call (224) 246-1461
              </a>
            </div>
          </div>
        </div>
      </section>

      {insta.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8">
          <Reveal>
            <div className="text-center mb-6">
              <h2 className="font-display text-3xl sm:text-4xl tracking-tight flex items-center justify-center gap-2.5">
                <Instagram size={26} strokeWidth={1.6} className="text-[#B84A3B]" /> Follow Us on Instagram
              </h2>
              <a href="https://instagram.com/kurticonnection_usa" target="_blank" rel="noreferrer" data-testid="insta-handle-link"
                className="overline text-[#6E6E69] mt-2 inline-block hover:text-[#B84A3B] transition-colors">
                @kurticonnection · Tap a photo to see our latest looks
              </a>
            </div>
          </Reveal>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {insta.map((src, i) => (
              <a key={i} href="https://instagram.com/kurticonnection_usa" target="_blank" rel="noreferrer" data-testid={`insta-${i}`} className="group relative block aspect-square overflow-hidden bg-[#EFEFEA]">
                <img src={src} alt="Instagram" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                <div className="absolute inset-0 bg-[#B84A3B]/0 group-hover:bg-[#B84A3B]/30 transition-colors" />
              </a>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

const ProductRow = ({ title, subtitle, to, products, onAdd, newBadge = false }) => (
  <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14" data-testid={`row-${title.toLowerCase().replace(/\s/g, "-")}`}>
    <Reveal>
      <div className="flex items-end justify-between mb-8">
        <div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-[#6E6E69] mt-1">{subtitle}</p>}
        </div>
        <Link to={to} className="hidden sm:inline-flex items-center gap-1.5 text-[0.7rem] font-mono tracking-[0.18em] uppercase border-b border-[#1A1A1A] pb-0.5 hover:text-[#B84A3B] hover:border-[#B84A3B] transition-colors">View All <ArrowRight size={12} /></Link>
      </div>
    </Reveal>
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-10">
      {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onAdd={onAdd} newBadge={newBadge} />)}
    </div>
  </section>
);
